/* eslint-disable no-nested-ternary */
/* eslint-disable camelcase */
/* eslint-disable react/display-name */
/**
 * @file EducationalMaterialElementsView.jsx
 * @author Daniel Aguirre
 * @description Education Material Management View Page
 */

// Dependencies
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Row, Col, Button } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useParams } from 'react-router';

// Helpers
import { educationElementType } from '@nephro/commons/helpers/utils';
import { getStorageItem } from 'commons/helpers/storage-helper';
import { STAGE_INDICATOR } from '@nephro/pages/Indicators/helpers/constants';
import { getPatientStage } from '@nephro/pages/Indicators/helpers/utils';

// Hooks
import usePatientIndicators from '@nephro/pages/Indicators/helpers/usePatientIndicators';

// Components
import ElementFile from '../components/elementFile';

const CreateButton = styled(Button)`
  margin-left: 8px;
  border-color: var(--color-baxter-teal-medium) !important;
  background-color: var(--color-baxter-teal-medium) !important;
  color: white;
  box-sizing: border-box;
  border-radius: 5px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: auto;
`;

/**
 * EducationalMaterial Management View
 * @param { Object } educationalMaterialActions - EducationalMaterial Actions.
 * @return { Node } React node.
 */

function EducationalMaterialElementsView({
  educationalElementActions,
  s3FileActions
}) {
  const { id: categoryId } = useParams();
  const { t: translate } = useTranslation();
  const [stage] = usePatientIndicators(STAGE_INDICATOR);
  const history = useHistory();
  const location = useLocation();
  const [files, setfiles] = useState([]);
  const [patStage, setPatStage] = useState(null);
  const [isPatient, setIsPatient] = useState(true);

  const { getEducationalElementByCategoryId, deleteEducationalElementById } =
    educationalElementActions;

  const { deleteS3FileByKey } = s3FileActions;

  const { permissions } = location.state;

  /**
   * @param {string} extension file extension
   * @returns return an icon based on extension provided
   */
  const getIconByType = (type) => {
    const [fileItem] = educationElementType.filter((itm) => itm.id === type);
    return fileItem?.icon || null;
  };

  const onGetData = (_, resp) => {
    if (resp?.data) setfiles(resp?.data);
  };

  const onGetEducationalElements = () => {
    getEducationalElementByCategoryId({ categoryId }, onGetData);
  };

  useEffect(async () => {
    if (stage) {
      const stageValue = getPatientStage(stage.valor);
      setPatStage(stageValue);
    }
    setIsPatient(await getStorageItem('isPatient'));
    onGetEducationalElements();
  }, [stage]);

  const onOpenDelete = (item) => {
    const data = {
      id: item.id_educational_element
    };
    deleteS3FileByKey({ key: item.uri });
    deleteEducationalElementById(data, onGetEducationalElements);
  };

  const onGetElementCol = (file, index, date) => {
    const category = file?.education_category.name;
    return (
      <Col xs={24} md={12} key={index}>
        <ElementFile
          name={file.name}
          onDeleteFunc={() => onOpenDelete(file)}
          onEditFunc={() =>
            history.push(`${location.pathname}/edit`, {
              action: 'edit',
              elementData: { ...file },
              category: location.state
            })
          }
          permissions={permissions}
          link={`/main/educational-material/element/${category}/${file.name}/${file.uri}`}
          linkParam={file}
          icon={getIconByType(file.type)}
          date={date}
        />
      </Col>
    );
  };
  return (
    <div className="py-5 px-7 h-full">
      <div className="flex justify-end mb-8">
        {!isPatient && permissions.save && (
          <Col xs={24} md={5}>
            <CreateButton
              onClick={() =>
                history.push(`${location.pathname}/load`, {
                  action: 'create',
                  elementData: null,
                  category: location.state
                })
              }
              icon={<UploadOutlined style={{ color: 'white' }} />}>
              {translate('dashboard.uploadMaterial')}
            </CreateButton>
          </Col>
        )}
      </div>
      <section>
        <Row gutter={20} className="pb-4">
          {files?.map((file, index) => {
            const date = new Date(file.created_at).toLocaleDateString();
            return !isPatient
              ? onGetElementCol(file, index, date)
              : patStage && file[`stage${patStage}`] === 1
              ? onGetElementCol(file, index, date)
              : null;
          })}
        </Row>
      </section>
    </div>
  );
}

/**
 * Component definition of proptypes.
 */
EducationalMaterialElementsView.propTypes = {
  educationalElementActions: PropTypes.shape({
    getEducationalElementByCategoryId: PropTypes.func.isRequired,
    deleteEducationalElementById: PropTypes.func.isRequired
  }),
  s3FileActions: PropTypes.shape({
    getS3FilesArray: PropTypes.func.isRequired,
    uploadS3File: PropTypes.func.isRequired,
    deleteS3FileByKey: PropTypes.func.isRequired
  }).isRequired
};

export default EducationalMaterialElementsView;
